import type { Clues } from './makeQueries';
import { brandHits } from './brandMatch';

const RESTAURANT_TYPES = new Set(['restaurant','food','cafe','bakery','bar','meal_takeaway','meal_delivery']);

function deburrLower(s?: string) {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokens(s?: string) {
  return deburrLower(s).split(' ').filter(Boolean);
}

function jaccard(a: string[], b: string[]) {
  if (a.length === 0 || b.length === 0) return 0;
  const A = new Set(a);
  const B = new Set(b);
  let inter = 0;
  for (const t of A) if (B.has(t)) inter++;
  const uni = A.size + B.size - inter;
  return uni === 0 ? 0 : inter / uni;
}

export function scoreCandidate(place: any, c: Clues): { score: number; reasons: string[] } {
  const reasons: string[] = [];
  const name = deburrLower(place?.name);
  const addr = deburrLower(place?.formatted_address);
  const types: string[] = Array.isArray(place?.types) ? place.types : [];

  let s = 0;
  const baseBeforeAddr = 0; // used to compute addr lift after base; simple since base starts at 0
  
  // Strong anchors required
  let hasStrongAnchor = false;
  let exactNameAnchor = false;

  // Name overlap with target
  if (c?.targetName) {
    const t = deburrLower(c.targetName);
    const sim = jaccard(tokens(name), tokens(t));
    if (sim >= 0.8) { s += 0.42; reasons.push('Exact/near-exact name match'); exactNameAnchor = true; }
    else if (sim >= 0.5) { s += 0.28; reasons.push('Strong name overlap'); }
    else if (sim >= 0.25) { s += 0.16; reasons.push('Partial name overlap'); }
  }

  // Phrases/hints overlap (title/caption/hashtags derived)
  if (Array.isArray(c?.phrases)) {
    for (const ph of c.phrases.slice(0, 3)) {
      const p = deburrLower(ph);
  if (!p) continue;
  if (name.includes(p)) { s += 0.10; reasons.push(`Name contains “${ph}”`); continue; }
  if (addr.includes(p)) { s += 0.06; reasons.push(`Address mentions “${ph}”`); continue; }
  // Token-level similarity for phrases with stopwords (e.g., 'en')
  const pt = tokens(p);
  const nt = tokens(name);
  const at = tokens(addr);
  const ns = jaccard(nt, pt);
  if (ns >= 0.6) { s += 0.10; reasons.push(`Name overlaps “${ph}”`); continue; }
  const as = jaccard(at, pt);
  if (as >= 0.5) { s += 0.06; reasons.push(`Address overlaps “${ph}”`); }
    }
  }

  // City/loc hint
  if (c?.cityGuess) {
    const city = deburrLower(c.cityGuess);
    if (city && addr.includes(city)) { s += 0.12; reasons.push('City/neighborhood match'); }
  }

  // Cuisine/dish tokens bonus
  const cu = c?.cuisine ? tokens(c.cuisine) : [];
  const dish = c?.dish ? tokens(c.dish) : [];
  if (cu.length) {
    const sim = jaccard(tokens(name).concat(tokens(addr)), cu);
    if (sim >= 0.2) { s += 0.06; reasons.push('Cuisine hint overlap'); }
  }
  if (dish.length) {
    const sim = jaccard(tokens(name).concat(tokens(addr)), dish);
    if (sim >= 0.2) { s += 0.04; reasons.push('Dish hint overlap'); }
  }

  // Type bonus
  if (types.some((t) => RESTAURANT_TYPES.has(t))) { s += 0.06; reasons.push('Restaurant-like type'); }

  // Strong brand verification from OCR tokens (if provided via Clues)
  const ocrTokens = Array.isArray((c as any).ocrBrandTokens) ? (c as any).ocrBrandTokens as string[] : [];
  if (ocrTokens.length && place?.name) {
    const hits = brandHits(ocrTokens, place.name);
    if (hits.length) { 
      s = Math.min(1, s + 0.40); 
      reasons.push(`vision-name:${hits.join(',')}`); 
      hasStrongAnchor = true; // OCR brand match is strong anchor
    }
  }

  // Address alignment bonus when OCR surfaced address-like text
  const ocrAddr = Array.isArray((c as any).ocrAddr) ? (c as any).ocrAddr as string[] : [];
  let addrLift = 0;
  if (ocrAddr.length && place?.formatted_address) {
    const rawAddr = place.formatted_address || '';
    const addrTok = tokens(rawAddr);
    let overlapScore = 0;
    let hasStreetAndNumber = false;
    let coloniaHit = false;
    let cpFromOcr: string | null = null;
    for (const a of ocrAddr) {
      const at = tokens(a);
      if (/\b\d{5}\b/.test(a)) cpFromOcr = (a.match(/\b\d{5}\b/) || [null])[0];
      const sim = jaccard(addrTok, at);
      if (sim >= 0.25) overlapScore = Math.max(overlapScore, sim);
      if (/(calle|c\.|av\.|avenida|blvd\.|boulevard|eje|calz\.|calzada|prol\.|prolongacion|paseo|mz\.|manzana|lt\.|lote|no\.|#|int\.|interior|dept\.|depto|col\.|colonia)\b/i.test(a) && /\b\d{1,4}[a-z0-9\-]?\b/i.test(a)) {
        hasStreetAndNumber = true;
      }
      if (/(porvenir|roma|n[aá]poles|ju[aá]rez|polanco|del valle|azcapotzalco)/i.test(a)) coloniaHit = true;
    }
  if (cpFromOcr) {
      if (rawAddr.includes(cpFromOcr)) { 
        // Reduce zipcode boost and only treat as strong anchor if combined with name evidence
        s = Math.min(1, s + 0.08);
        reasons.push('addr:cp-match'); 
        if (ocrTokens.length > 0 || exactNameAnchor) {
          hasStrongAnchor = true;
        }
      }
      else { s = Math.max(0, s - 0.35); reasons.push('addr:cp-mismatch'); }
  }
    if (overlapScore > 0 && hasStreetAndNumber) { s = Math.min(1, s + 0.15); reasons.push('addr:phrase-match'); }
    if (coloniaHit) { s = Math.min(1, s + 0.08); reasons.push('loc-hint'); }
  addrLift = Math.max(0, s - baseBeforeAddr);
  if (addrLift > 0.001) reasons.push('addr:boost');
  }

  // Must be restaurant type to proceed
  const isRestaurantType = types.some((t) => RESTAURANT_TYPES.has(t));
  if (!isRestaurantType) {
    s = Math.min(s, 0.3); // Cap non-restaurant places very low
    reasons.push('not-restaurant-type');
  }

  // Popularity tie-breaker
  const ratings = typeof place?.user_ratings_total === 'number' ? place.user_ratings_total : 0;
  const popularity = Math.min(ratings / 500, 0.1); // Reduced bonus
  if (popularity > 0) reasons.push(`Popularity bonus +${popularity.toFixed(2)}`);
  s = Math.min(1, s + popularity);

  // Apply anchor penalty - without strong anchors, cap score at 0.6
  if (!hasStrongAnchor) {
    s = Math.min(s, 0.6);
    reasons.push('no-strong-anchor');
  }

  return { score: Math.max(0, s), reasons };
}

export function pickTop(cands: any[], clues: Clues) {
  const scored = cands.map((p) => {
    const { score, reasons } = scoreCandidate(p, clues);
    return { p, score, reasons };
  }).sort((a, b) => b.score - a.score);
  const top = scored[0];
  const second = scored[1];
  return { scored, top, second };
}